import React, { useState, useEffect } from "react";
import { Calendar, MapPin, Award, ArrowRight, X, ZoomIn, Camera } from "lucide-react";

const POSTER = "https://customer-assets.emergentagent.com/job_mare-method-shop-1/artifacts/jjuxjzuo_48d780b0-a6de-4e00-8415-8d7ddaaa6523.png";
const EVENT_DATE = new Date("2026-08-29T18:00:00+02:00");

function useCountdown(target) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target.getTime() - now.getTime());
  const past = target.getTime() < now.getTime();
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return { past, days, hours, mins, secs };
}

function CountdownOrRecap() {
  const { past, days, hours, mins, secs } = useCountdown(EVENT_DATE);
  if (past) {
    return (
      <div data-testid="event-recap" className="mt-6 bg-[#0E7C8F] text-[#F9F8F6] p-6">
        <div className="flex items-start gap-3">
          <Camera size={28} className="text-[#F4E8D6] flex-shrink-0" strokeWidth={1.4} />
          <div>
            <p className="overline text-[#F4E8D6]">Evento concluso</p>
            <p className="font-serif-display text-2xl mt-1 leading-tight">
              Guarda le foto e il racconto della giornata
            </p>
            <p className="mt-3 text-sm font-light text-[#F9F8F6]/80">
              Grazie a chi era presente al Premio Pula Letteraria 2026. La galleria fotografica
              sarà disponibile qui a breve.
            </p>
            <button
              type="button"
              disabled
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 border border-[#F4E8D6]/40 text-[#F4E8D6]/70 text-xs uppercase tracking-[0.2em] cursor-not-allowed"
            >
              Galleria in arrivo
            </button>
          </div>
        </div>
      </div>
    );
  }
  const units = [
    { n: days, label: "Giorni" },
    { n: hours, label: "Ore" },
    { n: mins, label: "Minuti" },
    { n: secs, label: "Secondi" },
  ];
  return (
    <div data-testid="event-countdown" className="mt-6 bg-[#F4E8D6]/60 border-l-4 border-[#E89B6C] p-5">
      <p className="overline text-[#0B192C]/70 mb-3">Mancano</p>
      <div className="grid grid-cols-4 gap-2 md:gap-3">
        {units.map((u) => (
          <div key={u.label} className="text-center bg-white border border-[#0B192C]/10 py-3 md:py-4">
            <p className="font-serif-display text-2xl md:text-4xl leading-none text-[#0B192C] tabular-nums">
              {String(u.n).padStart(2, "0")}
            </p>
            <p className="text-[9px] md:text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B] mt-1.5">
              {u.label}
            </p>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-[#3A4F6B] text-center">
        al Premio Pula Letteraria · 29 Agosto 2026
      </p>
    </div>
  );
}

export default function UpcomingEvent() {
  const [open, setOpen] = useState(false);

  return (
    <section
      id="upcoming-event"
      data-testid="upcoming-event-section"
      className="py-20 md:py-28 bg-[#F4E8D6]/40"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#0B192C]/30 bg-white mb-6">
            <Calendar size={14} className="text-[#0B192C]" />
            <span className="overline text-[#0B192C]">Prossimo evento</span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#0B192C]">
            Un nuovo <em className="text-[#E89B6C]">traguardo</em>
            <br />
            nel mio percorso da autore.
          </h2>
        </div>

        <div className="grid lg:grid-cols-[1.1fr,1fr] gap-10 lg:gap-14 items-start">
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-track="event-poster-open"
            data-testid="event-poster-thumb"
            className="group relative block w-full overflow-hidden border-2 border-[#D4B895] hover:border-[#E89B6C] transition-all bg-[#0B1B3A]"
          >
            <img
              src={POSTER}
              alt="Premio Internazionale Pula Letteraria 2026 — Michele Roccaro"
              className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-[#0B192C]/0 group-hover:bg-[#0B192C]/30 transition-colors flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-2 px-5 py-2.5 bg-[#E89B6C] text-[#0B192C] text-xs uppercase tracking-[0.2em]">
                <ZoomIn size={14} /> Ingrandisci
              </span>
            </div>
          </button>

          <article className="space-y-5 text-base md:text-lg font-light leading-relaxed text-[#0B192C]">
            <div className="bg-[#0B192C] text-[#F9F8F6] p-6 -mx-1">
              <div className="flex items-start gap-4">
                <Award size={28} className="text-[#D4B895] flex-shrink-0 mt-1" strokeWidth={1.4} />
                <div>
                  <p className="overline text-[#D4B895]">Premio Internazionale</p>
                  <p className="font-serif-display text-2xl md:text-3xl mt-1 leading-tight">
                    Pula Letteraria 2026
                  </p>
                  <div className="mt-4 space-y-1.5 text-sm font-light text-[#F9F8F6]/85">
                    <p className="inline-flex items-center gap-2">
                      <Calendar size={13} className="text-[#E89B6C]" /> Sabato 29 Agosto 2026
                    </p>
                    <p className="inline-flex items-center gap-2">
                      <MapPin size={13} className="text-[#E89B6C]" /> Città di Pula (CA) · Sardegna
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <CountdownOrRecap />

            <p>
              Con grande emozione sono lieto di annunciare la mia partecipazione al{" "}
              <strong className="font-medium">Premio Internazionale Pula Letteraria 2026</strong>,
              che si terrà il <strong className="font-medium">29 agosto 2026</strong> nella splendida cornice di Pula (CA), Sardegna.
            </p>
            <p>
              Sarò presente con il mio libro <em className="text-[#0E7C8F]">&ldquo;Metodo di Mare – Lezioni di metodo, sicurezza,
              leadership e come diventare un professionista nel mondo digitale&rdquo;</em>, un&apos;opera
              nata dall&apos;esperienza maturata tra il mare, la formazione, la leadership e l&apos;innovazione digitale.
            </p>
            <p className="border-l-2 border-[#E89B6C] pl-5 italic text-[#0B192C]">
              &ldquo;Il mare insegna che ogni rotta inizia con il coraggio di salpare.&rdquo;
            </p>
            <p className="text-sm text-[#3A4F6B]">
              Desidero ringraziare gli organizzatori per questa opportunità. Qualunque sarà il
              risultato finale, essere parte di un evento che valorizza la cultura e la scrittura
              è già un grande motivo di orgoglio.
            </p>

            <div className="pt-4 border-t border-[#0B192C]/15">
              <p className="font-serif-display text-xl text-[#0B192C]">Michele Roccaro</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#3A4F6B] mt-1">
                Imprenditore Digitale · Formatore · Autore
              </p>
            </div>

            <button
              onClick={() => setOpen(true)}
              data-track="event-poster-cta"
              data-testid="event-poster-cta"
              className="group inline-flex items-center gap-3 px-6 py-3 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.2em] hover:bg-[#E89B6C] hover:text-[#0B192C] transition-all mt-2"
            >
              Vedi locandina ufficiale
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </button>
          </article>
        </div>
      </div>

      {open && (
        <div
          onClick={() => setOpen(false)}
          data-testid="event-poster-lightbox"
          className="fixed inset-0 z-50 bg-[#0B192C]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 overflow-y-auto cursor-zoom-out"
        >
          <button
            onClick={() => setOpen(false)}
            className="absolute top-6 right-6 text-[#F9F8F6] hover:text-[#E89B6C] bg-[#0B192C]/80 p-2 border border-[#F9F8F6]/20 z-10"
            aria-label="Chiudi"
          >
            <X size={22} />
          </button>
          <img
            src={POSTER}
            alt="Poster Pula Letteraria 2026"
            className="max-w-full h-auto cursor-default"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
